/**
 * 字典缓存 (模块级懒加载 + Promise 去重 + 兜底)
 *
 * 背景: 烘干厂前端此前从未接入后端字典, bizType 文案靠硬编码 map. 现改为
 *       优先查字典, 但字典接口异常时必须回落本地常量, 否则页面会白屏
 *       (request.js 对 success=false 直接 reject).
 *
 * 用法:
 *   import { loadDict, getDictLabel, getDictOptions } from '@/utils/dictionaryCache'
 *   // 页面 mounted 触发预加载 (不强制)
 *   mounted() { loadDict('drying_account_biz_type') }
 *   // 同步取文案 (未加载/失败时返回 undefined, 调用方需自行回落)
 *   const label = getDictLabel('drying_account_biz_type', 'input_inbound_return')
 *   // 下拉选项 (异步, 返回 Promise)
 *   const opts = await getDictOptions('drying_stock_biz_type')
 */
import { getDictItems } from '@/api/dryingDict'

// dictCode → Map<itemValue, itemText>
const cache = new Map()
// dictCode → Promise (进行中的请求, 去重并发)
const pending = new Map()
// dictCode → boolean (拉取是否成功过)
const loaded = new Map()

/**
 * 懒加载某字典到缓存. 并发调用同一 dictCode 只发一次请求 (Promise 去重).
 * 失败时缓存置空 Map 且不抛错 (静默失败, getDictLabel 返回 undefined 触发回落).
 * @returns {Promise<Map<string,string>>} 该字典的 value→text 映射
 */
export function loadDict(dictCode) {
  if (!dictCode) return Promise.resolve(new Map())
  if (loaded.get(dictCode)) return Promise.resolve(cache.get(dictCode) || new Map())
  if (pending.has(dictCode)) return pending.get(dictCode)

  const p = getDictItems(dictCode)
    .then(items => {
      const m = new Map()
      if (Array.isArray(items)) {
        // DictModel 字段: { value, text }
        items.forEach(it => {
          if (it && it.value != null) m.set(String(it.value), it.text || it.value)
        })
      }
      cache.set(dictCode, m)
      loaded.set(dictCode, true)
      return m
    })
    .catch(err => {
      // 静默失败: 标记为已加载(空Map), 后续不再重试刷屏; getDictLabel 返回 undefined 让调用方回落
      // eslint-disable-next-line no-console
      console.warn(`[dictCache] 字典 ${dictCode} 加载失败, 将回落本地常量:`, err && err.message)
      cache.set(dictCode, new Map())
      loaded.set(dictCode, true)
      return cache.get(dictCode)
    })
    .finally(() => {
      pending.delete(dictCode)
    })

  pending.set(dictCode, p)
  return p
}

/**
 * 同步取文案. 若字典尚未加载则触发懒加载(异步), 本次返回 undefined.
 * 调用方应做回落: getDictLabel(...) || fallbackMap[t] || t
 * @returns {string|undefined}
 */
export function getDictLabel(dictCode, value) {
  if (!dictCode || value == null) return undefined
  const m = cache.get(dictCode)
  if (m) return m.get(String(value))
  // 未加载: 触发懒加载 (下次渲染就能拿到), 本次返回 undefined
  loadDict(dictCode)
  return undefined
}

/**
 * 异步取下拉选项 [{ value, label }]. 首次调用触发加载, 后续走缓存.
 * @returns {Promise<Array<{value:string,label:string}>>}
 */
export function getDictOptions(dictCode) {
  return loadDict(dictCode).then(m => {
    const arr = []
    m.forEach((text, value) => arr.push({ value, label: text }))
    return arr
  })
}

/** 字典是否已加载成功 (调试用) */
export function isDictLoaded(dictCode) {
  return !!loaded.get(dictCode)
}
